import React, { useCallback, useEffect, useState } from "react";
import { Table, Switch, InputNumber, Select, Button, Modal, Input, message, Popconfirm, Tag, Tooltip, Empty, Typography } from "antd";
import { PlusOutlined, WarningOutlined, MailOutlined, CopyOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import config from "../../config";
import * as api from "./controller";
import HtmlBodyEditor from "./HtmlBodyEditor";
import OfficeReportsReviewDrawer from "./OfficeReportsReviewDrawer";
import OnboardingStepsPanel from "./OnboardingStepsPanel";
import MailboxesPanel from "./MailboxesPanel";
import PlansSettingsPanel from "./billing/PlansSettingsPanel";
import SalesTaxPanel from "./billing/SalesTaxPanel";
import "./crm.css";
import "./reportsReview.css";
import "./leadSettings.css";

/**
 * Super-admin rollout view for the per-office value reports.
 *
 * The FTC's own Reports page configures one clinic at a time, which is right for
 * the person doing the work and useless for the person switching the feature on.
 * `enabled` defaults off per office, so without this page nobody can see that no
 * reports are going out anywhere.
 *
 * The "Ready" column is the point. A schedule can read ON and still send nothing —
 * nobody assigned to sign it, or nobody approved to receive it — and a switch that
 * looks enabled while no mail arrives is worse than one that is plainly off.
 */
function OfficeReportsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // "The request failed" and "there is nothing configured" are DIFFERENT, and the
  // empty state here asserts a fact about the account. Rendering "no FTC is
  // assigned to any clinic" after a 500 is a confident lie about configuration —
  // and the thing it hides (an unmigrated DB, a permissions error) is exactly what
  // someone rolling this out needs to see.
  const [failed, setFailed] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.getReportSchedules();
    const ok = r.status >= 200 && r.status < 300 && Array.isArray(r.json);
    setFailed(!ok);
    setRows(ok ? r.json : []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (officeId, body) => {
    const r = await api.updateReportSchedule(Object.assign({ office_id: officeId }, body));
    if (r.status >= 200 && r.status < 300) {
      setRows((rs) => rs.map((x) => (x.office_id === officeId ? r.json : x)));
    } else {
      message.error((r.json && r.json.detail) || "Could not update that schedule.");
      load();
    }
  };

  const columns = [
    { title: "Clinic", dataIndex: "office_name" },
    {
      title: "Sends",
      width: 110,
      render: (_, r) => (
        <Switch
          size="small"
          checked={r.enabled}
          onChange={(v) => patch(r.office_id, { enabled: v })}
        />
      ),
    },
    {
      title: "Cadence",
      width: 130,
      render: (_, r) => (
        <Select
          size="small"
          value={r.cadence}
          onChange={(v) => patch(r.office_id, { cadence: v })}
          options={[
            { value: "monthly", label: "Monthly" },
            { value: "weekly", label: "Weekly" },
          ]}
          style={{ width: 110 }}
        />
      ),
    },
    {
      title: "Signed by",
      render: (_, r) => (
        <Select
          size="small"
          value={r.sender_id}
          placeholder="Any assigned FTC"
          onChange={(v) => patch(r.office_id, { sender_id: v || null })}
          options={(r.ftc_options || []).map((o) => ({
            value: o.id,
            label: `${o.email} (${o.contact_count} contact${o.contact_count === 1 ? "" : "s"})`,
          }))}
          style={{ width: "100%", minWidth: 220 }}
        />
      ),
    },
    {
      title: "Ready",
      width: 130,
      render: (_, r) =>
        r.blocked_reason ? (
          <Tooltip title={r.blocked_reason}>
            <Tag color="warning" icon={<WarningOutlined />}>Won&rsquo;t send</Tag>
          </Tooltip>
        ) : (
          <Tag color="success">
            {r.recipient_count} recipient{r.recipient_count === 1 ? "" : "s"}
          </Tag>
        ),
    },
    {
      title: "Last sent",
      width: 120,
      render: (_, r) =>
        r.last_sent_at ? new Date(r.last_sent_at).toLocaleDateString()
          : <span className="crm-sub">Never</span>,
    },
  ];

  const readyCount = rows.filter((r) => !r.blocked_reason).length;

  return (
    <>
      <div className="crm-inline-head">
        <p className="crm-sub" style={{ margin: 0, flex: 1 }}>
          Only clinics an FTC is assigned to correspond with appear here — a clinic
          with nobody assigned cannot receive a report at all. Assign one under
          <strong> FTC Users &rarr; Correspondence</strong>.
        </p>
        <Button
          icon={<MailOutlined />}
          disabled={!rows.length}
          onClick={() => setReviewOpen(true)}
        >
          Review {readyCount ? `${readyCount} ready` : "reports"}
        </Button>
      </div>
      <Table
        rowKey="office_id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        locale={{
          emptyText: loading ? null : failed ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <>
                  <div>Couldn&rsquo;t load the report schedules.</div>
                  <div className="crm-sub" style={{ marginTop: 4 }}>
                    The server returned an error — this is not the same as nothing
                    being configured.
                  </div>
                  <Button size="small" style={{ marginTop: 10 }} onClick={load}>
                    Try again
                  </Button>
                </>
              }
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No FTC is assigned to any clinic yet, so no reports can be sent."
            />
          ),
        }}
      />
      <OfficeReportsReviewDrawer
        open={reviewOpen}
        rows={rows}
        onClose={() => setReviewOpen(false)}
        onSent={load}
      />
    </>
  );
}

function ReminderRulesPanel({ templates }) {
  const [rules, setRules] = useState([]);
  const [edits, setEdits] = useState({}); // step_key -> {delay_days, repeat_days, template}

  const load = useCallback(async () => {
    const r = await api.getReminderRules();
    if (r.status >= 200 && r.status < 300 && Array.isArray(r.json)) {
      setRules(r.json);
      const e = {};
      r.json.forEach((x) => { e[x.step_key] = { delay_days: x.delay_days, repeat_days: x.repeat_days, template: x.template }; });
      setEdits(e);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (stepKey, body) => {
    const r = await api.updateReminderRule(stepKey, body);
    if (r.status >= 200 && r.status < 300) {
      setRules((rs) => rs.map((x) => (x.step_key === stepKey ? r.json : x)));
    } else {
      message.error((r.json && r.json.detail) || "Could not update rule.");
      load();
    }
  };

  const setEdit = (stepKey, key, val) =>
    setEdits((e) => ({ ...e, [stepKey]: { ...e[stepKey], [key]: val } }));

  const templateOptions = [{ value: null, label: "— none —" }].concat(
    templates.map((t) => ({ value: t.id, label: t.name }))
  );

  return (
    <div>
      <p className="crm-sub">
        When enabled, each office still missing a step is emailed the chosen template,
        starting after the delay and repeating on the cadence (0 = once). Reminders
        also require the O365 email + reminders flags to be on server-side.
      </p>
      <Table
        size="small" rowKey="step_key" pagination={false}
        dataSource={rules}
        columns={[
          { title: "Milestone", dataIndex: "label" },
          {
            title: "On", dataIndex: "enabled", width: 70, align: "center",
            render: (v, r) => <Switch size="small" checked={v} onChange={(val) => patch(r.step_key, { enabled: val })} />,
          },
          {
            title: "Start after (days)", width: 150, align: "center",
            render: (_, r) => (
              <InputNumber
                min={0} size="small"
                value={(edits[r.step_key] || {}).delay_days}
                onChange={(v) => setEdit(r.step_key, "delay_days", v)}
              />
            ),
          },
          {
            title: "Repeat every (days)", width: 150, align: "center",
            render: (_, r) => (
              <InputNumber
                min={0} size="small"
                value={(edits[r.step_key] || {}).repeat_days}
                onChange={(v) => setEdit(r.step_key, "repeat_days", v)}
              />
            ),
          },
          {
            title: "Template", width: 200,
            render: (_, r) => (
              <Select
                size="small" style={{ width: "100%" }}
                value={(edits[r.step_key] || {}).template}
                options={templateOptions}
                onChange={(v) => setEdit(r.step_key, "template", v)}
              />
            ),
          },
          {
            title: "", key: "save", width: 70, align: "right",
            render: (_, r) => (
              <Button
                size="small" type="link"
                onClick={() => {
                  const e = edits[r.step_key] || {};
                  patch(r.step_key, {
                    delay_days: e.delay_days, repeat_days: e.repeat_days, template_id: e.template || "",
                  });
                }}
              >
                Save
              </Button>
            ),
          },
        ]}
      />
    </div>
  );
}

function TemplateEditor({ open, template, tokens, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setName(template ? template.name : "");
    setSubject(template ? template.subject : "");
    setBody(template ? template.body_html : "");
    setActive(template ? template.active : true);
  }, [template, open]);

  const save = async () => {
    if (!name.trim() || !subject.trim()) {
      message.error("Name and subject are required.");
      return;
    }
    setBusy(true);
    const payload = { name, subject, body_html: body, active };
    const r = template
      ? await api.updateTemplate(template.id, payload)
      : await api.createTemplate(payload);
    setBusy(false);
    if (r.status >= 200 && r.status < 300) {
      message.success("Template saved.");
      onSaved && onSaved();
      onClose();
    } else {
      message.error((r.json && r.json.detail) || "Could not save template.");
    }
  };

  return (
    <Modal
      title={template ? "Edit template" : "New template"}
      open={open}
      onCancel={onClose}
      onOk={save}
      confirmLoading={busy}
      okText="Save"
      width={640}
    >
      <Input addonBefore="Name" value={name} onChange={(e) => setName(e.target.value)} />
      <Input addonBefore="Subject" style={{ marginTop: 8 }} value={subject} onChange={(e) => setSubject(e.target.value)} />
      <HtmlBodyEditor rows={9} style={{ marginTop: 8 }} placeholder="Body (HTML). Use tokens like {office_name}." value={body} onChange={setBody} />
      <div style={{ marginTop: 8 }}>
        <span className="crm-sub">Tokens: </span>
        {tokens.map((t) => (
          <Tag key={t.token} style={{ cursor: "pointer", marginBottom: 4 }} onClick={() => setBody((b) => `${b}{${t.token}}`)}>
            {`{${t.token}}`}
          </Tag>
        ))}
      </div>
      <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center" }}>
        <Switch checked={active} onChange={setActive} /> <span>Active</span>
      </div>
    </Modal>
  );
}

function TemplatesPanel({ templates, tokens, reload }) {
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const del = async (t) => {
    const r = await api.deleteTemplate(t.id);
    if (r.status >= 200 && r.status < 300) { message.success("Deleted."); reload(); }
    else message.error("Could not delete.");
  };

  return (
    <div>
      <div style={{ textAlign: "right", marginBottom: 10 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); setOpen(true); }}>
          New template
        </Button>
      </div>
      <Table
        size="small" rowKey="id" pagination={false}
        dataSource={templates}
        columns={[
          { title: "Name", dataIndex: "name" },
          { title: "Subject", dataIndex: "subject" },
          { title: "Active", dataIndex: "active", width: 80, align: "center", render: (v) => (v ? <Tag color="green">Active</Tag> : <Tag>Off</Tag>) },
          {
            title: "", key: "a", align: "right", width: 150,
            render: (_, t) => (
              <>
                <Button size="small" type="link" onClick={() => { setEditing(t); setOpen(true); }}>Edit</Button>
                <Popconfirm title="Delete template?" onConfirm={() => del(t)} okText="Delete" okButtonProps={{ danger: true }}>
                  <Button size="small" type="link" danger>Delete</Button>
                </Popconfirm>
              </>
            ),
          },
        ]}
      />
      <TemplateEditor open={open} template={editing} tokens={tokens} onClose={() => setOpen(false)} onSaved={reload} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Leads
// ---------------------------------------------------------------------------

// Lead templates use a different token vocabulary from office templates (there
// is no office yet — no readiness, no carriers), served by the same endpoint
// under ?kind=lead. This file is the only caller, so the helper lives here
// rather than widening controller.js. Same auth header shape as controller.js
// (note the double space after "Token" — it matches api.service.js).
function leadTokenAuthHeader() {
  const user = JSON.parse(localStorage.getItem("user"));
  return user && user.key ? { Authorization: "Token  " + user.key } : {};
}
async function fetchLeadTokens() {
  try {
    const res = await fetch(`${config.apiGateway.URL}/crm/email-templates/tokens/?kind=lead`, {
      headers: Object.assign({ "Content-Type": "application/json" }, leadTokenAuthHeader()),
    });
    let json = null;
    try { json = await res.json(); } catch (e) { json = null; }
    return { json, status: res.status };
  } catch (e) {
    return { json: null, status: 0 };
  }
}

// dayjs's relativeTime plugin isn't registered anywhere in this app, and one
// label doesn't justify wiring it. Coarse on purpose: "3 d ago" is what the
// question ("is the form still reaching us?") needs.
function relativeFromNow(iso) {
  if (!iso) return null;
  const d = dayjs(iso);
  if (!d.isValid()) return null;
  const mins = dayjs().diff(d, "minute");
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days} d ago`;
  return d.format("MMM D, YYYY");
}

// Viewers' clipboards are inconsistent (http origins, older WebViews), so fall
// back to the textarea trick rather than failing a "Copy" button silently.
async function copyText(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
}

// The antd Tag presets a lead tag may use. "" renders antd's default grey.
const TAG_COLORS = ["purple", "gold", "cyan", "green", "red", "blue", "orange", "magenta", "volcano", "geekblue", "lime"];
const TAG_COLOR_OPTIONS = [{ value: "", label: <span><span className="leadset-swatch" style={{ background: "#e5e4ea" }} />None</span> }]
  .concat(TAG_COLORS.map((c) => ({
    value: c,
    label: <span><Tag color={c} style={{ marginRight: 8, lineHeight: "16px" }}>&nbsp;</Tag>{c}</span>,
  })));

/**
 * The automatic "book a call" email to website leads. Two settings and one
 * template: the switch and the template choice save on change (a Save button
 * on two controls invites forgetting it), the template opens in the same editor
 * the other templates use, with the lead token list instead of the office one.
 */
function LeadBookingEmailPanel({ templates, reloadTemplates }) {
  const [settings, setSettings] = useState(null);
  const [failed, setFailed] = useState(false);
  const [leadTokens, setLeadTokens] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);

  const load = useCallback(async () => {
    const r = await api.getLeadSettings();
    const ok = r.status >= 200 && r.status < 300 && r.json && typeof r.json === "object";
    setFailed(!ok);
    setSettings(ok ? r.json : null);
  }, []);
  useEffect(() => {
    load();
    fetchLeadTokens().then((r) => {
      if (r.status >= 200 && r.status < 300 && Array.isArray(r.json)) setLeadTokens(r.json);
    });
  }, [load]);

  const patch = async (body) => {
    const r = await api.updateLeadSettings(body);
    if (r.status >= 200 && r.status < 300 && r.json) {
      setSettings(r.json);
    } else {
      message.error((r.json && r.json.detail) || "Could not save the lead email settings.");
      load();
    }
  };

  const templateOptions = templates.map((t) => ({
    value: t.id,
    label: t.active ? t.name : `${t.name} (inactive)`,
  }));
  const chosen = settings && settings.booking_template_id
    ? templates.find((t) => t.id === settings.booking_template_id) || null
    : null;

  if (failed) {
    return (
      <div className="leadset-panel">
        <p className="leadset-panel-title">Booking email</p>
        <p className="crm-sub leadset-panel-sub">
          Couldn&rsquo;t load the lead email settings.{" "}
          <Button size="small" type="link" style={{ padding: 0 }} onClick={load}>Try again</Button>
        </p>
      </div>
    );
  }

  return (
    <div className="leadset-panel">
      <p className="leadset-panel-title">Booking email</p>
      <p className="crm-sub leadset-panel-sub">
        Sent once, right after a website submission creates a lead. Leads you add by
        hand are not emailed unless you tick &ldquo;send the booking email&rdquo; on the form.
      </p>
      {settings && settings.email_enabled === false && (
        <div className="leadset-warn">
          <WarningOutlined />
          Email integration is off on the server, so nothing will be sent. The switch still saves.
        </div>
      )}
      <div className="leadset-row">
        <div className="leadset-row-label">Send the booking email automatically to new website leads</div>
        <div className="leadset-row-control">
          <Switch
            checked={!!(settings && settings.auto_booking_email_enabled)}
            disabled={!settings}
            onChange={(v) => patch({ auto_booking_email_enabled: v })}
          />
        </div>
      </div>
      <div className="leadset-row">
        <div className="leadset-row-label">
          Template
          <div className="leadset-hint">The template should contain your calendar link.</div>
        </div>
        <div className="leadset-row-control">
          <Select
            style={{ width: 260 }}
            placeholder="Choose a template"
            value={chosen ? chosen.id : undefined}
            options={templateOptions}
            disabled={!settings}
            // Setting "" clears it server-side; the Select's clear button maps to that.
            allowClear
            onChange={(v) => patch({ booking_template_id: v === undefined ? "" : v })}
          />
          <Button disabled={!chosen} onClick={() => setEditorOpen(true)}>Edit template</Button>
        </div>
      </div>
      {settings && settings.booking_template_id && !chosen && (
        <p className="leadset-hint">
          The saved template (&ldquo;{settings.booking_template_name || "unknown"}&rdquo;) is no longer in
          the list — it may have been deleted. Choose another.
        </p>
      )}
      <TemplateEditor
        open={editorOpen}
        template={chosen}
        tokens={leadTokens}
        onClose={() => setEditorOpen(false)}
        onSaved={reloadTemplates}
      />
    </div>
  );
}

/**
 * Global lead tags. Small on purpose: name, colour, description. Editing is
 * inline in the row so the table stays the single source of what exists —
 * a separate modal for three fields is more UI than the job.
 */
function LeadTagsPanel() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState({ name: "", color: "", description: "" });
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({ name: "", color: "", description: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.getLeadTags();
    const ok = r.status >= 200 && r.status < 300 && Array.isArray(r.json);
    setFailed(!ok);
    setTags(ok ? r.json : []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const name = draft.name.trim();
    if (!name) { message.error("Give the tag a name."); return; }
    setAdding(true);
    const r = await api.createLeadTag({ name, color: draft.color || "", description: draft.description.trim() });
    setAdding(false);
    if (r.status >= 200 && r.status < 300) {
      setDraft({ name: "", color: "", description: "" });
      load();
    } else if (r.status === 409) {
      message.error("A tag with that name already exists.");
    } else {
      message.error((r.json && r.json.detail) || "Could not create the tag.");
    }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setEdit({ name: t.name, color: t.color || "", description: t.description || "" });
  };
  const saveEdit = async () => {
    const name = edit.name.trim();
    if (!name) { message.error("Give the tag a name."); return; }
    setSavingEdit(true);
    const r = await api.updateLeadTag(editingId, { name, color: edit.color || "", description: edit.description.trim() });
    setSavingEdit(false);
    if (r.status >= 200 && r.status < 300) {
      setEditingId(null);
      load();
    } else if (r.status === 409) {
      message.error("A tag with that name already exists.");
    } else {
      message.error((r.json && r.json.detail) || "Could not update the tag.");
    }
  };
  const remove = async (t) => {
    const r = await api.deleteLeadTag(t.id);
    if (r.status >= 200 && r.status < 300) load();
    else message.error((r.json && r.json.detail) || "Could not delete the tag.");
  };

  const columns = [
    {
      title: "Tag", width: 200,
      render: (_, t) => editingId === t.id ? (
        <div style={{ display: "flex", gap: 6 }}>
          <Input size="small" value={edit.name} onChange={(e) => setEdit((d) => ({ ...d, name: e.target.value }))} style={{ width: 120 }} />
          <Select size="small" value={edit.color} options={TAG_COLOR_OPTIONS} onChange={(v) => setEdit((d) => ({ ...d, color: v }))} style={{ width: 110 }} />
        </div>
      ) : (
        <Tag color={t.color || undefined} style={{ margin: 0 }}>{t.name}</Tag>
      ),
    },
    {
      title: "Description",
      render: (_, t) => editingId === t.id ? (
        <Input size="small" value={edit.description} onChange={(e) => setEdit((d) => ({ ...d, description: e.target.value }))} />
      ) : (
        t.description ? t.description : <span className="crm-sub">—</span>
      ),
    },
    {
      title: "Leads", width: 80, align: "right",
      render: (_, t) => <span className="leadset-num">{t.lead_count || 0}</span>,
    },
    {
      title: "", key: "a", align: "right", width: 150,
      render: (_, t) => editingId === t.id ? (
        <>
          <Button size="small" type="link" loading={savingEdit} onClick={saveEdit}>Save</Button>
          <Button size="small" type="link" onClick={() => setEditingId(null)}>Cancel</Button>
        </>
      ) : (
        <>
          <Button size="small" type="link" onClick={() => startEdit(t)}>Edit</Button>
          <Popconfirm
            title="Delete this tag?"
            description={
              t.lead_count
                ? `It will be removed from ${t.lead_count} lead${t.lead_count === 1 ? "" : "s"}.`
                : "It will be removed from all leads."
            }
            onConfirm={() => remove(t)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" type="link" danger>Delete</Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div className="leadset-panel">
      <p className="leadset-panel-title">Lead tags</p>
      <p className="crm-sub leadset-panel-sub">
        Labels for sorting leads and practices — a source, a campaign, a reason they
        went quiet. Anyone can also type a new tag straight into a lead&rsquo;s tag picker.
      </p>
      <Table
        size="small" rowKey="id" pagination={false}
        loading={loading}
        dataSource={tags}
        columns={columns}
        locale={{
          emptyText: loading ? null : failed ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <>
                  <div>Couldn&rsquo;t load the tags.</div>
                  <Button size="small" style={{ marginTop: 10 }} onClick={load}>Try again</Button>
                </>
              }
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No tags yet. Add one below." />
          ),
        }}
      />
      <div className="leadset-tag-add">
        <Input
          placeholder="Tag name"
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          onPressEnter={add}
          style={{ width: 160 }}
        />
        <Select
          value={draft.color}
          options={TAG_COLOR_OPTIONS}
          onChange={(v) => setDraft((d) => ({ ...d, color: v }))}
          style={{ width: 130 }}
        />
        <Input
          placeholder="Description (optional)"
          value={draft.description}
          onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
          onPressEnter={add}
          style={{ flex: 1, minWidth: 180 }}
        />
        <Button type="primary" icon={<PlusOutlined />} loading={adding} onClick={add}>Add tag</Button>
      </div>
    </div>
  );
}

/**
 * Everything the site developer needs to point the Framer form at us, and
 * everything an admin needs to tell whether it is still arriving. The secret
 * itself is never returned by the API — only its length — so this panel can be
 * shown to anyone with CRM access.
 */
// Addresses and domains that never become leads from mail — filled by "Not a
// lead" in the lead drawer, seeded with insurance carriers, editable here.
function IgnoredSendersPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.getIgnoredSenders();
    setRows(r.status >= 200 && r.status < 300 && Array.isArray(r.json) ? r.json : []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const v = value.trim();
    if (!v) return;
    setAdding(true);
    const r = await api.addIgnoredSender(v);
    setAdding(false);
    if (r.status >= 200 && r.status < 300) {
      setValue("");
      load();
    } else {
      message.error((r.json && r.json.detail) || "Couldn't add that.");
    }
  };
  const remove = async (row) => {
    const r = await api.deleteIgnoredSender(row.id);
    if (r.status >= 200 && r.status < 300) load();
    else message.error("Couldn't remove it.");
  };

  return (
    <div className="leadset-panel">
      <p className="leadset-panel-title">Ignored senders</p>
      <p className="crm-sub leadset-panel-sub">
        Mail from these never makes a lead. A domain covers its subdomains too
        (sunlife.com covers provider.sunlife.com). Their mail is still filed under
        any office or lead it belongs to.
      </p>
      <Table
        size="small" rowKey="id" loading={loading} dataSource={rows}
        pagination={rows.length > 10 ? { pageSize: 10, size: "small" } : false}
        locale={{ emptyText: loading ? null : <div className="crm-empty">Nothing ignored yet.</div> }}
        columns={[
          { title: "Address or domain", dataIndex: "value", render: (v, r) => (
            <span><span className="crm-name">{r.kind === "domain" ? `@${v}` : v}</span></span>
          ) },
          { title: "Why", dataIndex: "note", render: (v) => <span className="crm-sub">{v || "—"}</span> },
          { title: "", key: "a", width: 80, align: "right", render: (_, r) => (
            <Popconfirm title="Let their mail make leads again?" okText="Remove" onConfirm={() => remove(r)}>
              <Button size="small" type="link" danger>Remove</Button>
            </Popconfirm>
          ) },
        ]}
      />
      <div className="leadset-tag-add">
        <Input
          placeholder="jane@vendor.com or vendor.com"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onPressEnter={add}
          style={{ width: 260 }}
        />
        <Button type="primary" icon={<PlusOutlined />} loading={adding} onClick={add}>Ignore</Button>
      </div>
    </div>
  );
}

function FramerWebhookPanel() {
  const [info, setInfo] = useState(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.getFramerWebhookInfo();
    const ok = r.status >= 200 && r.status < 300 && r.json && typeof r.json === "object";
    setFailed(!ok);
    setInfo(ok ? r.json : null);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const fields = (info && Array.isArray(info.fields)) ? info.fields : [];
  const copyNames = async () => {
    const ok = await copyText(fields.map((f) => f.name).join("\n"));
    if (ok) message.success(`Copied ${fields.length} input name${fields.length === 1 ? "" : "s"}.`);
    else message.error("Couldn't copy — select the names in the table instead.");
  };

  const last = info ? relativeFromNow(info.last_received_at) : null;
  const bySource = info && info.leads_by_source && typeof info.leads_by_source === "object"
    ? Object.entries(info.leads_by_source).filter(([, n]) => n) : [];
  const SOURCE_LABELS = { framer: "website", manual: "manual", backfill: "backfill" };

  return (
    <div className="leadset-panel">
      <p className="leadset-panel-title">Website form (Framer)</p>
      <p className="crm-sub leadset-panel-sub">
        Submissions from the marketing site arrive here as leads. Nothing to switch on:
        once the form posts to the URL with the right secret, they show up under Leads.
      </p>
      {failed ? (
        <p className="crm-sub">
          Couldn&rsquo;t load the webhook details.{" "}
          <Button size="small" type="link" style={{ padding: 0 }} onClick={load}>Try again</Button>
        </p>
      ) : loading || !info ? (
        <p className="crm-sub">Loading…</p>
      ) : (
        <>
          <div className="leadset-url">
            <Typography.Paragraph copyable={{ text: info.url }}>
              <code>{info.url}</code>
            </Typography.Paragraph>
          </div>
          <div className="leadset-facts">
            {info.configured ? (
              <Tooltip title={info.secret_length ? `${info.secret_length} characters` : null}>
                <span className="crm-status-pill on">Secret configured</span>
              </Tooltip>
            ) : (
              <span className="crm-status-pill off">Not configured — set FRAMER_WEBHOOK_SECRET on the server</span>
            )}
            <span>
              Last submission:{" "}
              {last ? (
                <Tooltip title={dayjs(info.last_received_at).format("MMM D, YYYY HH:mm")}><strong>{last}</strong></Tooltip>
              ) : <strong>never</strong>}
            </span>
            <span><strong>{info.submissions_30d || 0}</strong> submission{info.submissions_30d === 1 ? "" : "s"} in the last 30 days</span>
            {info.unmatched_30d > 0 && (
              <Tooltip
                title={
                  info.last_unmatched_keys && info.last_unmatched_keys.length
                    ? `The last one sent these input names: ${info.last_unmatched_keys.join(", ")}`
                    : null
                }
              >
                <span className="crm-status-pill off">
                  {info.unmatched_30d} submission{info.unmatched_30d === 1 ? "" : "s"} had no email and made no lead — check the form&rsquo;s input names
                </span>
              </Tooltip>
            )}
            {bySource.length > 0 && (
              <span className="crm-sub">
                Leads by source: {bySource.map(([k, n]) => `${SOURCE_LABELS[k] || k} ${n}`).join(" · ")}
              </span>
            )}
          </div>
          <div className="crm-inline-head">
            <p className="crm-sub" style={{ margin: 0, flex: 1 }}>
              Input names the form must use. Anything else the form sends is kept and
              shown under the lead&rsquo;s &ldquo;Other answers&rdquo;.
            </p>
            <Button size="small" icon={<CopyOutlined />} disabled={!fields.length} onClick={copyNames}>
              Copy names
            </Button>
          </div>
          <Table
            size="small" rowKey="name" pagination={false}
            dataSource={fields}
            columns={[
              { title: "Input name", dataIndex: "name", width: 220, render: (v) => <span className="crm-code">{v}</span> },
              { title: "Label", dataIndex: "label" },
              { title: "Required", dataIndex: "required", width: 100, align: "center", render: (v) => (v ? <Tag color="purple">Required</Tag> : <span className="crm-sub">optional</span>) },
            ]}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="The server returned no field list." /> }}
          />
          <p className="leadset-steps">
            In Framer, select the form &rarr; <strong>Send To</strong> &rarr; <strong>Add…</strong> &rarr;{" "}
            <strong>Webhook</strong> &rarr; paste the URL above, set the same secret, and name each
            input exactly as listed.
          </p>
        </>
      )}
    </div>
  );
}

export default function CrmSettings() {
  const [templates, setTemplates] = useState([]);
  const [tokens, setTokens] = useState([]);

  const reload = useCallback(async () => {
    const r = await api.getTemplates();
    if (r.status >= 200 && r.status < 300 && Array.isArray(r.json)) setTemplates(r.json);
  }, []);
  useEffect(() => {
    reload();
    api.getTemplateTokens().then((r) => {
      if (r.status >= 200 && r.status < 300 && Array.isArray(r.json)) setTokens(r.json);
    });
  }, [reload]);

  return (
    <div className="crm-settings">
      <div className="crm-section">
        <div className="crm-section-head"><span className="crm-section-title">Registration onboarding</span></div>
        <div className="crm-section-body"><OnboardingStepsPanel /></div>
      </div>
      <div className="crm-section">
        <div className="crm-section-head"><span className="crm-section-title">Plans &amp; trials</span></div>
        <div className="crm-section-body"><PlansSettingsPanel /></div>
      </div>
      <div className="crm-section">
        <div className="crm-section-head"><span className="crm-section-title">Sales tax</span></div>
        <div className="crm-section-body"><SalesTaxPanel /></div>
      </div>
      <div className="crm-section">
        <div className="crm-section-head"><span className="crm-section-title">Email inboxes</span></div>
        <div className="crm-section-body"><MailboxesPanel /></div>
      </div>
      <div className="crm-section">
        <div className="crm-section-head"><span className="crm-section-title">Readiness reminders</span></div>
        <div className="crm-section-body"><ReminderRulesPanel templates={templates} /></div>
      </div>
      <div className="crm-section">
        <div className="crm-section-head"><span className="crm-section-title">Leads</span></div>
        <div className="crm-section-body">
          <LeadBookingEmailPanel templates={templates} reloadTemplates={reload} />
          <LeadTagsPanel />
          <IgnoredSendersPanel />
          <FramerWebhookPanel />
        </div>
      </div>
      <div className="crm-section">
        <div className="crm-section-head"><span className="crm-section-title">Clinic value reports</span></div>
        <div className="crm-section-body"><OfficeReportsPanel /></div>
      </div>
      <div className="crm-section">
        <div className="crm-section-head"><span className="crm-section-title">Email templates</span></div>
        <div className="crm-section-body"><TemplatesPanel templates={templates} tokens={tokens} reload={reload} /></div>
      </div>
    </div>
  );
}
